/**
 * @file src/pages/inside/tenant/style.js
 * @description Styles for Tenant Pages
 * @version 1.0.0
 */

import styled from 'styled-components';
import { Card } from 'antd';

export const DashboardContainer = styled.div`
    width: 100%;
    max-width: 100%;
    overflow-x: hidden;
    background-color: ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return isDark ? '#1a1a1a' : '#f0f0f0';
    }};
    min-height: 100vh;
    box-sizing: border-box;
    position: relative;

    @media (max-width: 1024px) {
        padding: ${({ theme }) => theme.spacing.xl} 24px ${({ theme }) => theme.spacing.xl} 20px;
    }

    @media (max-width: 768px) {
        padding: ${({ theme }) => theme.spacing.lg} 16px ${({ theme }) => theme.spacing.lg} 16px;
    }

    @media (max-width: 480px) {
        padding: ${({ theme }) => theme.spacing.md} 12px ${({ theme }) => theme.spacing.md} 12px;
    }
`;

export const KPICardsGrid = styled.div`
    display: grid;
    grid-template-columns: ${({ $columns }) => `repeat(${Math.min(Number($columns) || 6, 6)}, minmax(0, 1fr))`};
    gap: 14px;
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
    height: ${({ $height }) => $height || 'auto'};
    align-self: stretch;

    /* Each cell expands equally; minmax(0,1fr) + min-width avoids overflow in narrow layouts */
    & > * {
        height: auto;
        min-height: 108px;
        min-width: 0;
        width: 100%;
        overflow: hidden;
    }

    @media (max-width: 1024px) {
        grid-template-columns: repeat(3, minmax(0, 1fr));
        gap: 12px;
        
        & > * {
            height: auto;
        }
    }

    @media (max-width: 768px) {
        grid-template-columns: repeat(2, 1fr);
        gap: 10px;
    }

    @media (max-width: 480px) {
        grid-template-columns: 1fr;
        gap: 10px;
    }
`;

export const TopSectionContainer = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    gap: 16px;
    width: 100%;
    margin-bottom: 24px;
    align-items: start;

    @media (max-width: 1024px) {
        grid-template-columns: 1fr;
        gap: 16px;
    }

    @media (max-width: 768px) {
        gap: 12px;
        margin-bottom: 16px;
    }

    @media (max-width: 480px) {
        gap: 10px;
        margin-bottom: 12px;
    }
`;

export const FirstRowContainer = styled.div`
    display: grid;
    grid-template-columns: 35fr 40fr; /* 35% for KPICard, 40% for Bar Chart (relative to 75% parent) */
    gap: 16px;
    width: 100%;
    height: 330px;
    min-height: 330px;
    align-items: stretch;

    @media (max-width: 1400px) {
        height: 300px;
        min-height: 300px;
    }

    @media (max-width: 1024px) {
        grid-template-columns: 1fr;
        gap: 16px;
        height: auto;
        min-height: auto;
    }

    @media (max-width: 768px) {
        gap: 12px;
    }

    @media (max-width: 480px) {
        gap: 10px;
    }
`;

export const DeliveryTrackingWrapper = styled.div`
    width: 100%;
    height: 660px;
    min-height: 660px;

    @media (max-width: 1400px) {
        height: 600px;
        min-height: 600px;
    }

    @media (max-width: 1024px) {
        height: auto;
        min-height: auto;
    }
`;

export const LeftColumn = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    width: 100%;
    min-width: 0;
    overflow: hidden;
    height: 100%;
    align-items: stretch;

    @media (max-width: 768px) {
        gap: 12px;
    }

    @media (max-width: 480px) {
        gap: 10px;
    }
`;

export const ChartsColumn = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    width: 100%;
    max-width: 100%;
    min-width: 0;
    height: 100%;
    box-sizing: border-box;
    overflow: visible !important;

    & > * {
        height: 100%;
        min-height: 0;
        overflow: visible !important;
        flex: 1;
    }

    @media (max-width: 1024px) {
        width: 100%;
        max-width: 100%;
        gap: 16px;
        
        & > * {
            height: auto;
            min-height: auto;
            flex: none;
        }
    }

    @media (max-width: 768px) {
        gap: 12px;
    }

    @media (max-width: 480px) {
        gap: 10px;
    }
`;

export const ChartCard = styled(Card)`
    background: ${({ theme, $red, $dark }) => {
        const isDark = theme.colors.background.default === '#141414';
        if ($red) return '#F04A4A';
        if ($dark) return '#292929';
        return isDark ? '#292929' : '#ffffff';
    }} !important;
    border-radius: 40px;
    border: ${({ theme, $red, $dark }) => {
        const isDark = theme.colors.background.default === '#141414';
        if ($red || $dark) return 'none';
        return `1px solid ${isDark ? '#2C2C2C' : '#E5E5E5'}`;
    }} !important;
    box-shadow: ${({ $red, $dark }) => ($red || $dark) ? 'none' : '0 2px 8px rgba(0, 0, 0, 0.1)'};
    height: ${({ $red, $totalRevenue }) => {
        if ($red) return 'auto';
        if ($totalRevenue) return '100%';
        return '100%';
    }};
    display: flex;
    flex-direction: column;
    width: 100%;
    max-width: 100%;
    min-width: 0;
    box-sizing: border-box;
    overflow: visible !important;

    @media (max-width: 1024px) {
        border-radius: 32px;
    }

    @media (max-width: 768px) {
        border-radius: 24px;
    }

    @media (max-width: 480px) {
        border-radius: 20px;
    }

    .ant-card-body {
        padding: ${({ $dark, $totalRevenue }) => {
        if ($totalRevenue) return '26px 30px';
        return $dark ? '20px' : '16px';
    }};
        height: ${({ $red, $totalRevenue }) => {
        if ($red) return '100%';
        if ($totalRevenue) return 'auto';
        return '100%';
    }};
        min-height: ${({ $totalRevenue }) => $totalRevenue ? '463px' : 'auto'};
        display: flex;
        flex-direction: column;
        justify-content: ${({ $totalRevenue }) => $totalRevenue ? 'space-between' : 'flex-start'};
        overflow: visible !important;

        @media (max-width: 1024px) {
            padding: ${({ $dark, $totalRevenue }) => {
        if ($totalRevenue) return '22px 24px';
        return $dark ? '18px' : '14px';
    }};
            min-height: ${({ $totalRevenue }) => $totalRevenue ? '300px' : 'auto'};
        }

        @media (max-width: 768px) {
            padding: ${({ $dark, $totalRevenue }) => {
        if ($totalRevenue) return '18px 20px';
        return $dark ? '16px' : '12px';
    }};
            min-height: ${({ $totalRevenue }) => $totalRevenue ? '250px' : 'auto'};
        }

        @media (max-width: 480px) {
            padding: ${({ $dark, $totalRevenue }) => {
        if ($totalRevenue) return '16px 16px';
        return $dark ? '14px' : '10px';
    }};
        }
    }
`;

export const ChartCardTitle = styled.div`
    font-size: ${({ $dark, $totalRevenue }) => {
        if ($totalRevenue) return '14px';
        return $dark ? '16px' : '14px';
    }};
    font-weight: ${({ $dark, $totalRevenue }) => $totalRevenue ? '400' : '600'};
    color: ${({ theme, $dark, $totalRevenue }) => {
        const isDark = theme.colors.background.default === '#141414';
        if ($totalRevenue) return isDark ? '#8A98A7' : '#8A98A7';
        return $dark ? '#ffffff' : (isDark ? '#ffffff' : '#000000');
    }};
    margin-bottom: ${({ $totalRevenue }) => $totalRevenue ? '0' : '16px'};
    font-family: var(--font-family-figtree);
    
    @media (max-width: 768px) {
        font-size: ${({ $dark, $totalRevenue }) => {
        if ($totalRevenue) return '12px';
        return $dark ? '14px' : '12px';
    }};
        margin-bottom: ${({ $totalRevenue }) => $totalRevenue ? '0' : '12px'};
    }
`;

export const ChartCardValue = styled.div`
    font-size: ${({ $totalRevenue }) => $totalRevenue ? '48px' : '24px'};
    font-weight: 600;
    color: ${({ theme, $dark, $totalRevenue }) => {
        const isDark = theme.colors.background.default === '#141414';
        if ($totalRevenue) return isDark ? '#ffffff' : '#000000';
        return $dark ? '#ffffff' : (isDark ? '#ffffff' : '#000000');
    }};
    font-family: var(--font-family-lufga);
    margin-bottom: ${({ $totalRevenue }) => $totalRevenue ? '8px' : '8px'};
    
    @media (max-width: 768px) {
        font-size: ${({ $totalRevenue }) => $totalRevenue ? '36px' : '20px'};
    }

    @media (max-width: 480px) {
        font-size: ${({ $totalRevenue }) => $totalRevenue ? '28px' : '18px'};
    }
`;

export const ChartCardChange = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: ${({ $totalRevenue }) => $totalRevenue ? '14px' : '12px'};
    font-weight: 500;
    color: ${({ theme, $positive, $dark, $totalRevenue }) => {
        const isDark = theme.colors.background.default === '#141414';
        if ($totalRevenue) return $positive ? '#52c41a' : '#ff4d4f';
        if ($dark) return $positive ? '#52c41a' : '#ff4d4f';
        return $positive ? '#52c41a' : '#ff4d4f';
    }};
    font-family: var(--font-family-figtree);
    
    img {
        width: ${({ $totalRevenue }) => $totalRevenue ? '16px' : '12px'};
        height: ${({ $totalRevenue }) => $totalRevenue ? '16px' : '12px'};
    }
    
    @media (max-width: 768px) {
        font-size: ${({ $totalRevenue }) => $totalRevenue ? '12px' : '10px'};
    }
`;

export const TotalRevenueHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 24px;
`;

export const TotalRevenueContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

export const TotalRevenueTitleRow = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
`;

export const TotalRevenueIcon = styled.div`
    img {
        width: 48px;
        height: 48px;
        
        @media (max-width: 768px) {
            width: 40px;
            height: 40px;
        }
    }
`;

export const ShippingRevenueWrapper = styled.div`
    width: 100%;
    margin-bottom: 24px;

    @media (max-width: 768px) {
        margin-bottom: 16px;
    }

    @media (max-width: 480px) {
        margin-bottom: 12px;
    }
`;

export const StudentsHeaderRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 16px;
    flex-wrap: wrap;
`;

export const StudentsHeaderLeft = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 240px;
`;

export const StudentsHeaderTitle = styled.div`
    font-family: var(--font-family-lufga);
    font-weight: 700;
    font-size: 18px;
    color: ${({ theme }) => theme.colors.text.primary};
`;

export const StudentsHeaderSub = styled.div`
    font-size: 13px;
    color: ${({ theme }) => theme.colors.text.secondary};
`;

export const StudentsHeaderActions = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
`;

/** School Time Configuration page — vertical stack inside tenant shell */
export const SchoolTimeStack = styled.div`
    display: flex;
    flex-direction: column;
    gap: 14px;
`;

export const SchoolTimeBranchGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(420px, 1fr));
    gap: 16px;
`;

export const SchoolTimeBranchCard = styled(Card)`
    border-radius: 18px;
    border: 1px solid rgba(15, 23, 42, 0.08);
    overflow: hidden;

    .ant-card-body {
        background: ${({ theme }) => {
            const isDark = theme.colors.background.default === '#141414';
            return isDark
                ? 'linear-gradient(180deg, rgba(255,255,255,0.04), rgba(255,255,255,0.02))'
                : 'linear-gradient(180deg, rgba(240, 253, 250, 0.75), rgba(255, 255, 255, 1))';
        }};
    }

    .ant-card-head-title {
        font-weight: 800;
    }
`;

export const SchoolTimeBranchHeaderRow = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
`;

export const SchoolTimeBranchHeaderLeft = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 10px;
    min-width: 0;
`;

export const SchoolTimeBranchIconWrap = styled.div`
    width: 36px;
    height: 36px;
    border-radius: 12px;
    background: rgba(240, 74, 74, 0.12);
    color: ${({ theme }) => theme.colors.primary};
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
`;

export const SchoolTimeBranchMeta = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
    min-width: 0;
`;

export const SchoolTimeBranchTitleText = styled.div`
    font-weight: 900;
    font-size: 15px;
    line-height: 1.2;
`;

export const SchoolTimeBranchSummaryLine = styled.div`
    margin-top: 2px;
    color: ${({ theme }) => theme.colors.text.secondary};
    font-size: 12px;
    line-height: 1.2;
`;

export const SchoolTimeBranchAddressLine = styled.div`
    color: ${({ theme }) => theme.colors.text.secondary};
    font-size: 12px;
    line-height: 1.3;
    min-height: 32px;
`;

export const SchoolTimeDaysList = styled.div`
    margin-top: 12px;
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

export const SchoolTimeDayRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 10px 12px;
    border: 1px solid ${({ theme }) => theme.colors.border.default};
    border-radius: 12px;
    background: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#1f1f1f' : '#ffffff')};
`;

export const SchoolTimeDayLeft = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 140px;
`;

export const SchoolTimeDayRight = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    justify-content: flex-end;
    flex: 1;
`;

export const SchoolTimeCardActions = styled.div`
    display: flex;
    gap: 8px;
    flex-shrink: 0;
`;

export const SchoolTimeCardActionSlot = styled.div`
    width: ${({ $width }) => `${Number($width) || 42}px`};
    flex-shrink: 0;

    .ant-btn {
        width: 100%;
        padding: 0;
        display: flex;
        align-items: center;
        justify-content: center;
    }
`;

export const SchoolTimeDatePickerWrap = styled.div`
    width: 100%;

    .ant-picker {
        width: 100%;
    }
`;

export const SchoolTimeSectionLabel = styled.div`
    margin-top: 10px;
    font-weight: 800;
    font-size: 13px;
`;

